import { useEffect, useMemo, useState } from "react";
import { BarChart3, BriefcaseBusiness, CheckCircle2, ClipboardList, Eye, EyeOff, LayoutDashboard, Loader2, Lock, LogOut, Mail, Shield, UserCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { canAccessRoute, getRoleHome, normalizeRole, roleLabels, type Role, type UserProfile } from "../lib/roles";
import { fetchCurrentProfile } from "../lib/api";

type View = {
  path: string;
  title: string;
  role: Role | "all";
  icon: typeof LayoutDashboard;
  description: string;
};

const views: View[] = [
  {
    path: "/employee",
    title: "Employee",
    role: "all",
    icon: UserCircle,
    description: "Submit and track leave, overtime, reschedule, budget, and liquidation requests."
  },
  {
    path: "/contractor",
    title: "Contractor",
    role: "contractor",
    icon: BriefcaseBusiness,
    description: "Contractor request tracking and reporting workspace."
  },
  {
    path: "/manager",
    title: "Manager",
    role: "manager",
    icon: ClipboardList,
    description: "Approve team requests, review work logs, and monitor assigned agents."
  },
  {
    path: "/finance",
    title: "Finance",
    role: "finance",
    icon: BarChart3,
    description: "Review finance approvals, invoices, payslips, expenses, and cash-flow reports."
  },
  {
    path: "/admin",
    title: "Admin",
    role: "admin",
    icon: Shield,
    description: "Manage users, roles, approvals, announcements, and system audit activity."
  }
];

export function App() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [path, setPath] = useState(window.location.pathname === "/" ? "/employee" : window.location.pathname);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname === "/" ? "/employee" : window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    refreshProfile();

    const { data } = supabase.auth.onAuthStateChange(() => {
      refreshProfile();
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const activeView = useMemo(() => views.find((view) => path.startsWith(view.path)) || views[0], [path]);
  const accessibleViews = useMemo(() => {
    if (!profile) return [];
    return views.filter((view) => view.role === "all" || canAccessRoute(profile.role, view.path));
  }, [profile]);

  async function refreshProfile() {
    setIsLoading(true);
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      setProfile(null);
      setIsLoading(false);
      return;
    }

    const result = await fetchCurrentProfile();
    if (result.success && result.data) {
      const nextProfile = { ...result.data, role: normalizeRole(result.data.role) };
      setProfile(nextProfile);

      if (!canAccessRoute(nextProfile.role, path)) {
        navigate(getRoleHome(nextProfile.role));
      }
    } else {
      setAuthError(result.error || "Unable to load your profile.");
    }

    setIsLoading(false);
  }

  function navigate(nextPath: string) {
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
  }

  async function logout() {
    await supabase.auth.signOut();
    setProfile(null);
    navigate("/login");
  }

  if (isLoading) {
    return (
      <main className="center-screen">
        <Loader2 className="spin" size={28} />
      </main>
    );
  }

  if (!profile || path === "/login") {
    return <LoginScreen error={authError} onAuthenticated={refreshProfile} />;
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">O</span>
          <span>
            <strong>ORBSS</strong>
            <small>Central Management</small>
          </span>
        </div>

        <nav className="nav-list" aria-label="Main navigation">
          {accessibleViews.map((view) => {
            const Icon = view.icon;
            const isActive = activeView.path === view.path;
            return (
              <button key={view.path} className={isActive ? "nav-item active" : "nav-item"} onClick={() => navigate(view.path)}>
                <Icon size={18} />
                <span>{view.title}</span>
              </button>
            );
          })}
        </nav>

        <button className="logout-button" onClick={logout}>
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <h1>{activeView.title}</h1>
            <p>{activeView.description}</p>
          </div>
          <div className="profile-pill">
            <CheckCircle2 size={18} />
            <span>{profile.name || profile.email}</span>
            <strong>{roleLabels[profile.role]}</strong>
          </div>
        </header>

        <RoleDashboard view={activeView} />
      </section>
    </main>
  );
}

function LoginScreen({ error, onAuthenticated }: { error: string | null; onAuthenticated: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(error);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
    if (loginError) {
      setMessage(loginError.message);
    } else {
      onAuthenticated();
    }

    setIsSubmitting(false);
  }

  return (
    <main className="login-screen">
      <div className="login-orb">
        <div className="neon-loader-ring" />
        <form className="login-panel" onSubmit={login}>
          <div className="login-heading">
            <img src="https://cdn-icons-png.freepik.com/512/295/295128.png" alt="ORBSS Logo" />
            <h1>ORBSS Central Management System</h1>
          </div>

          <label className="input-shell" aria-label="Email">
            <Mail size={18} />
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email" autoComplete="email" required />
          </label>

          <label className="input-shell" aria-label="Password">
            <Lock size={18} />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              required
            />
            <button type="button" className="icon-button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </label>

          <div className="login-options">
            <label className="remember-row">
              <input type="checkbox" />
              <span>Remember Me</span>
            </label>
          </div>

          {message ? <p className="form-message">{message}</p> : null}

          <button className="primary-button" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="spin" size={18} /> : null}
            <span>Login</span>
          </button>

          <div className="login-links">
            <button type="button">Forgot your password?</button>
            <span>|</span>
            <button type="button">Sign up</button>
          </div>

          <p className="version-text">v1.1.0</p>
        </form>
      </div>
    </main>
  );
}

function RoleDashboard({ view }: { view: View }) {
  const modules = [
    "Authentication migrated to Supabase sessions",
    "Role routing preserved from the Apps Script app",
    "Google Sheets data moves to Postgres tables gradually",
    "Google Drive, Docs, Gmail, and Chat can stay bridged during transition"
  ];

  return (
    <div className="dashboard-grid">
      <section className="module-panel">
        <h2>{view.title} workspace</h2>
        <p>This is the new Cloudflare/Supabase app shell. Module APIs can now be migrated from Apps Script one workflow at a time.</p>
      </section>

      {modules.map((module) => (
        <article className="status-card" key={module}>
          <CheckCircle2 size={20} />
          <span>{module}</span>
        </article>
      ))}
    </div>
  );
}
